import React, { Component } from 'react';
import _ from 'lodash';
import Papa from 'papaparse';
import {
  Table,
  Label,
  Input,
  Dropdown,
  Statistic,
  Segment,
  Loader,
  Message,
  Divider,
} from 'semantic-ui-react';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import hasRESTClient from '../../hocs/has-rest-client';

const SUMMARY_SEGMENT_STYLE = { flexShrink: 0 };
const CONTROLS_SEGMENT_STYLE = { flexShrink: 0 };
const CONTROLS_WRAPPER_STYLE = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '1rem',
  justifyContent: 'space-between',
};
const SEARCH_INPUT_STYLE = { flex: '1 1 260px' };
const STATUS_DROPDOWN_STYLE = { minWidth: '180px' };
const MODAL_CONTENT_STYLE = {
  minHeight: '600px',
  maxHeight: '600px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};
const TABLE_SEGMENT_STYLE = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
};
const TABLE_SCROLL_WRAPPER_STYLE = {
  flex: 1,
  overflowY: 'auto',
  paddingRight: '0.25rem',
};
const LOADER_SEGMENT_STYLE = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const SUMMARY_CONTAINER_STYLE = {
  padding: '8px',
  borderRadius: '6px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e3e6eb',
};

function getFullS3FilePath(key) {
  return `https://discussions-uploads-production.s3.eu-west-1.amazonaws.com/${key}`;
}

const parseMultipleS3Files = async filesNames => {
  const parsePromises = filesNames.map(fileKey =>
    new Promise((resolve, reject) => {
      Papa.parse(getFullS3FilePath(fileKey), {
        download: true,
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: results => resolve(results),
        error: err => reject(err),
      });
    }),
  );

  // Wait for all to finish (some may fail)
  const settled = await Promise.allSettled(parsePromises);

  // Keep only the fulfilled results
  // eslint-disable-next-line lodash/chain-style, lodash/chaining
  const fulfilled = _.chain(settled)
    .filter({ status: 'fulfilled' })
    .map('value')
    .value();

  return fulfilled;
};

// eslint-disable-next-line lodash/chain-style, lodash/chaining
const buildHandlesList = results => _.chain(results)
  .flatMap('data') // merge all `data` arrays
  .map('instagramUrl') // extract instagramUrl from each object
  .compact() // remove undefined/null
  .map(url => _.last(url.split('/').filter(Boolean))) // get last non-empty segment
  .uniq() // remove duplicates
  .value();

class CsvUploadStatusModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      isLoading: true,
      isLoaded: false,
      isError: false,
      searchQuery: '',
      statusFilter: 'all',
    };
  }

  async componentDidMount() {
    const { restClient, campaignId } = this.props;
    this.setState({ isLoading: true, isError: false });

    try {
      const campaign = await restClient.get(`/campaigns/${campaignId}?fields[campaigns]=csvFileUploads`);
      const csvFileUploads = _.get(campaign, 'data.data.attributes.csvFileUploads', []);

      if (!csvFileUploads.length) {
        this.setState({
          isLoaded: true,
          isError: false,
          isLoading: false,
          rows: [],
        });
        return;
      }

      const results = await parseMultipleS3Files(csvFileUploads);

      this.setState({
        rows: buildHandlesList(results) || [],
        isLoading: false,
        isLoaded: true,
        isError: false,
      });
    } catch (error) {
      this.setState({
        isError: true,
        isLoading: false,
        isLoaded: false,
        rows: [],
      });
      // eslint-disable-next-line no-console
      console.error('Failed to load CSV upload status', error);
    }
  }

  getHandleStatus = handle => {
    const { igHandles } = this.props;
    const isOnCampaign = _.includes(igHandles, handle);

    return isOnCampaign ? 'on-campaign' : 'missing';
  };

  renderHandleStatus = handle => {
    const status = this.getHandleStatus(handle);

    if (status === 'on-campaign') {
      return (
        <Label color="green" horizontal>
          On Campaign
        </Label>
      );
    }

    return (
      <Label color="red" horizontal>
        Missing
      </Label>);
  }

  render() {
    const { isLoading, isError, rows = [], searchQuery, statusFilter } = this.state;
    const { onClose } = this.props;

    const normalizedQuery = _.toLower(_.trim(searchQuery));
    const filteredRows = normalizedQuery
      ? rows.filter(handle => _.includes(_.toLower(handle), normalizedQuery))
      : rows;
    const filteredByStatus = statusFilter === 'all'
      ? filteredRows
      : filteredRows.filter(handle => {
        const status = this.getHandleStatus(handle);
        if (statusFilter === 'on-campaign') return status === 'on-campaign';
        if (statusFilter === 'missing') return status === 'missing';
        return true;
      });
    const hasRows = !_.isEmpty(filteredByStatus);
    const showData = !isLoading && !isError && rows.length > 0;
    const totalCount = rows.length;
    const onCampaignCount = rows.filter(handle => this.getHandleStatus(handle) === 'on-campaign').length;
    const missingCount = rows.filter(handle => this.getHandleStatus(handle) === 'missing').length;
    const statusOptions = [
      { key: 'all', value: 'all', text: 'All statuses' },
      { key: 'on-campaign', value: 'on-campaign', text: 'On campaign' },
      { key: 'missing', value: 'missing', text: 'Missing' },
    ];

    return (
      <DyzioModal
        headerText="CSV Import Status"
        size="large"
        onClose={onClose}
      >
        <div style={MODAL_CONTENT_STYLE}>
          {isLoading && (
            <Segment basic style={LOADER_SEGMENT_STYLE}>
              <Loader active inline="centered" content="Loading CSV uploads..." />
            </Segment>
          )}
          {!isLoading && isError && (
            <Message negative>
              <Message.Header>Failed to load CSV uploads</Message.Header>
              <p>Please try again in a moment.</p>
            </Message>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <Message info>
              <Message.Header>No CSV uploads found for this campaign</Message.Header>
              <p>Upload a CSV to see the latest statuses here.</p>
            </Message>
          )}
          {showData && (
            <div>
              <Segment basic style={SUMMARY_CONTAINER_STYLE}>
                <div style={SUMMARY_SEGMENT_STYLE}>
                  <Statistic.Group widths="three" size="tiny">
                    <Statistic horizontal size="tiny">
                      <Statistic.Value>{totalCount}</Statistic.Value>
                      <Statistic.Label>Total</Statistic.Label>
                    </Statistic>
                    <Statistic horizontal size="tiny" color="green">
                      <Statistic.Value>{onCampaignCount}</Statistic.Value>
                      <Statistic.Label>On Campaign</Statistic.Label>
                    </Statistic>
                    <Statistic horizontal size="tiny" color="red">
                      <Statistic.Value>{missingCount}</Statistic.Value>
                      <Statistic.Label>Missing</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                </div>
              </Segment>
              <Divider />
            </div>
          )}
          {showData && (
            <div>
              <div style={CONTROLS_SEGMENT_STYLE}>
                <div style={CONTROLS_WRAPPER_STYLE}>
                  <Input
                    icon="search"
                    placeholder="Search handles..."
                    value={searchQuery}
                    onChange={(event, { value }) => this.setState({ searchQuery: value })}
                    style={SEARCH_INPUT_STYLE}
                  />
                  <Dropdown
                    selection
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(event, { value }) => this.setState({ statusFilter: value })}
                    aria-label="Filter handles by status"
                    style={STATUS_DROPDOWN_STYLE}
                  />
                </div>
              </div>
              {hasRows && <Divider />}
            </div>
          )}
          {!isLoading && !isError && rows.length > 0 && !hasRows && (
            <Message warning>
              <Message.Header>No handles match your filters</Message.Header>
              <p>Adjust the search text or status filter to see handles in the table.</p>
            </Message>
          )}
          {hasRows && (
            <div style={TABLE_SEGMENT_STYLE}>
              <div style={TABLE_SCROLL_WRAPPER_STYLE}>
                <Table celled striped compact="very" size="small" stackable>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell collapsing>#</Table.HeaderCell>
                      <Table.HeaderCell>Handle</Table.HeaderCell>
                      <Table.HeaderCell collapsing>Status</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredByStatus.map((handle, index) => {
                      const rowStatus = this.getHandleStatus(handle);
                      return (
                        <Table.Row
                          key={`handle-${handle}`}
                          positive={rowStatus === 'on-campaign'}
                          negative={rowStatus === 'missing'}
                        >
                          <Table.Cell>{index + 1}</Table.Cell>
                          <Table.Cell>
                            {handle}
                          </Table.Cell>
                          <Table.Cell>
                            {this.renderHandleStatus(handle)}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </div>
            </div>
          )}
        </div>
      </DyzioModal>);
  }
}

export default hasRESTClient(CsvUploadStatusModal);



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/status-modal.js
 **/